/**
 * Lo que comparten los formularios de todas las historias (`order-form.ts` la
 * carta, `book-order-form.ts` el libro): los campos comunes, sus topes y cómo
 * se lee cada uno. Neutral a propósito: una historia no importa sus
 * primitivas del módulo de otra.
 */

/** Dónde vive el niño: la ciudad la nombra Papá Noel. */
export interface ChildLocation {
  city: string
  country?: string
}

/** Los campos que todo formulario de pedido trae, sea de la historia que sea. */
export interface BaseOrderForm {
  name: string
  age?: number
  location?: ChildLocation
  interests: string[]
  personalDetails: string[]
  parentMessage?: string
  /** Fase 1 no acepta foto del niño: siempre null. */
  photoPath: null
  language: string
  tone: string
}

/**
 * Topes de longitud por campo, en caracteres.
 *
 * Existen por dos motivos que se suman: el endpoint que alimenta esto es
 * PÚBLICO, sin autenticar y escribe en la base (spec §10), y todo lo que entra
 * acaba interpolado en el prompt del guionista, donde cada carácter se paga en
 * tokens. Sin topes, `name`, `parentMessage`, `tone` y cada entrada de los
 * arrays eran de longitud libre.
 *
 * Los números salen del uso real, con holgura deliberada:
 *
 * - `name` (80): es el nombre de pila de un niño, que Papá Noel pronuncia. Un
 *   nombre compuesto largo cabe cuatro veces.
 * - `city` / `country` (80): el topónimo más largo que alguien escribe de
 *   verdad (Llanfairpwllgwyngyll…, 58) cabe entero.
 * - entrada de lista (160): un hecho, un interés o un regalo es la frase corta
 *   que una escena nombra. Las escenas tienen presupuesto de 18-34 palabras, así
 *   que 160 caracteres ya exceden lo que el guion puede gastar en ella.
 * - `parentMessage` (500): la entrada más larga del formulario, y aun así el
 *   guion sólo le dedica el cierre (26-32 palabras).
 * - `tone` (60): es una etiqueta («cálido y mágico», 15), no texto libre.
 * - `language` (35): el máximo de una etiqueta BCP-47 bien formada.
 *
 * Se RECHAZA, nunca se trunca: recortar cambiaría lo que el padre escribió sin
 * decírselo y el vídeo saldría con media frase. El error viaja como mensaje del
 * contrato de dominio, que el caso de uso convierte en 400.
 *
 * Exportado: el formulario de `/video-papa-noel` pone sus `maxLength` desde
 * aquí, así que el tope vive en un solo sitio.
 */
export const MAX_LENGTH = {
  name: 80,
  city: 80,
  country: 80,
  listEntry: 160,
  parentMessage: 500,
  tone: 60,
  language: 35,
} as const

/**
 * Cota de `age`, en años cumplidos. Es el único campo numérico y el único que
 * no tenía tope: `typeof === 'number'` dejaba validar, persistir e interpolar
 * en el prompt del guionista un `-3`, un `0.5` o un `1e9`.
 *
 * 1-17 entero por el mismo criterio que sus vecinos, el uso real: el producto
 * es un vídeo de Papá Noel para un menor, la edad decide el registro con el
 * que le habla, y fuera de ese rango no hay ni niño al que hablarle (por
 * debajo) ni producto (a partir de 18). Se RECHAZA, no se recorta, igual que
 * las longitudes.
 */
export const AGE_RANGE = { min: 1, max: 17 } as const

function trimmedText(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function withinLimit(value: string, field: string, max: number): string {
  if (value.length > max) {
    throw new Error(`El campo «${field}» supera el máximo de ${max} caracteres.`)
  }
  return value
}

export function text(value: unknown, field: string, max: number): string | undefined {
  const trimmed = trimmedText(value)
  return trimmed === undefined ? undefined : withinLimit(trimmed, field, max)
}

/**
 * El recorte por NÚMERO de entradas se mantiene como estaba: es el presupuesto
 * que el guion puede gastar, no una validación. La longitud se comprueba
 * DESPUÉS del recorte, sobre las entradas que el vídeo va a usar de verdad.
 */
export function list(value: unknown, field: string, maxEntries: number): string[] {
  if (!Array.isArray(value)) return []
  return value
    .map(trimmedText)
    .filter((entry): entry is string => !!entry)
    .slice(0, maxEntries)
    .map((entry, index) => withinLimit(entry, `${field}[${index}]`, MAX_LENGTH.listEntry))
}

function readLocation(value: unknown): ChildLocation | undefined {
  if (!value || typeof value !== 'object') return undefined
  const raw = value as Record<string, unknown>
  const city = text(raw.city, 'location.city', MAX_LENGTH.city)
  return city ? { city, country: text(raw.country, 'location.country', MAX_LENGTH.country) } : undefined
}

function assertName(form: Record<string, unknown>): string {
  const name = text(form.name, 'name', MAX_LENGTH.name)
  if (!name) throw new Error('El pedido necesita el nombre del niño.')
  return name
}

/** Años cumplidos: entero (nada de `0.5`) dentro de `AGE_RANGE`. */
function isValidAge(value: number): boolean {
  if (!Number.isInteger(value)) return false
  return value >= AGE_RANGE.min && value <= AGE_RANGE.max
}

/**
 * `age` es OPCIONAL: lo que no llega como número se queda en `undefined`
 * («no lo sabemos»), igual que `text()` ignora lo que no es cadena. Lo que sí
 * llega como número tiene que caber en `AGE_RANGE`, o se rechaza.
 */
function readAge(form: Record<string, unknown>): number | undefined {
  if (typeof form.age !== 'number') return undefined
  if (!isValidAge(form.age)) {
    throw new Error(`La edad debe ser un número entero entre ${AGE_RANGE.min} y ${AGE_RANGE.max}.`)
  }
  return form.age
}

/** El cuerpo de la petición tiene que ser un objeto; cualquier otra cosa se rechaza antes de leer un campo. */
export function asFormObject(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object') throw new Error('El pedido debe ser un objeto.')
  return value as Record<string, unknown>
}

/**
 * Los campos comunes sin listas, que cada historia recorta a su presupuesto.
 * Los defaults de idioma y tono viven aquí y en ningún otro sitio.
 */
export function readCommonFields(form: Record<string, unknown>) {
  return {
    name: assertName(form),
    age: readAge(form),
    location: readLocation(form.location),
    parentMessage: text(form.parentMessage, 'parentMessage', MAX_LENGTH.parentMessage),
    photoPath: null,
    language: text(form.language, 'language', MAX_LENGTH.language) ?? 'es-ES',
    tone: text(form.tone, 'tone', MAX_LENGTH.tone) ?? 'cálido y mágico',
  } satisfies Omit<BaseOrderForm, 'interests' | 'personalDetails'>
}
